import { SPARKLINE_COLORS } from './ownerTheme'

type SparklineProps = {
  series: number[] | number[][]
  width?: number
  height?: number
  strokeWidth?: number
  className?: string
  colors?: string[]
}

const normalizeSeries = (series: number[] | number[][]) => {
  if (Array.isArray(series[0])) {
    return series as number[][]
  }
  return [series as number[]]
}

const getMinMax = (series: number[][]) => {
  let min = Number.POSITIVE_INFINITY
  let max = Number.NEGATIVE_INFINITY
  series.forEach((line) => {
    line.forEach((val) => {
      if (val < min) min = val
      if (val > max) max = val
    })
  })
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return { min: 0, max: 1 }
  }
  if (min === max) {
    return { min: min - 1, max: max + 1 }
  }
  return { min, max }
}

const buildPath = (data: number[], min: number, max: number, width: number, height: number) => {
  if (!data.length) return ''
  const step = data.length === 1 ? 0 : width / (data.length - 1)
  return data
    .map((val, index) => {
      const x = step * index
      const y = height - ((val - min) / (max - min)) * height
      return `${index === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`
    })
    .join(' ')
}

export default function Sparkline({
  series,
  width = 320,
  height = 120,
  strokeWidth = 2,
  className,
  colors = SPARKLINE_COLORS,
}: SparklineProps) {
  const normalized = normalizeSeries(series)
  const { min, max } = getMinMax(normalized)

  return (
    <svg
      className={className}
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      height={height}
      preserveAspectRatio="none"
    >
      {normalized.map((line, index) => {
        const path = buildPath(line, min, max, width, height)
        if (!path) return null
        return (
          <path
            key={`spark-${index}`}
            d={path}
            fill="none"
            stroke={colors[index % colors.length]}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity={index === 0 ? 1 : 0.75}
          />
        )
      })}
    </svg>
  )
}
